import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Edit2, Plus, Trash2, FileText } from "lucide-react";
import AppModal from "../../components/AppModal";
import DataTable from "../../components/DataTable";
import ResourcePage from "../../components/ResourcePage";
import { api } from "../../axios/api";
import { confirmAction, notify } from "../../utils/toastAlert";

const emptyForm = {
  descricao: "",
  referencia: "",
  valor: "",
  data: new Date().toISOString().split("T")[0],
  estado: "Paga",
};

const closeModal = (id) => {
  const element = document.getElementById(id);
  if (!element || !window.bootstrap) return;
  window.bootstrap.Modal.getOrCreateInstance(element).hide();
};

const showModal = (id) => {
  const element = document.getElementById(id);
  if (!element || !window.bootstrap) return;
  window.bootstrap.Modal.getOrCreateInstance(element).show();
};

const Despesas = () => {
  const user = useSelector((state) => state.user.user);
  const [despesas, setDespesas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);

  const fetchDespesas = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/despesas");
      const allDespesas = response.data?.data || response.data || [];
      const companyDespesas = allDespesas.filter(
        (item) => Number(item.empresa_id) === Number(user?.empresa_id)
      );
      setDespesas(companyDespesas);
    } catch (err) {
      setError(err.response?.data?.message || "Erro ao carregar despesas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.empresa_id) {
      fetchDespesas();
    }
  }, [user?.empresa_id]);

  const handleChange = (event) => {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const openEdit = (item = selected) => {
    if (!item) return;
    setEditing(item);
    setForm({
      descricao: item.descricao || "",
      referencia: item.referencia || "",
      valor: item.valor || "",
      data: item.data || new Date().toISOString().split("T")[0],
      estado: item.estado || "Paga",
    });
    showModal("despesaFormModal");
  };

  const payload = () => {
    const data = { ...form };
    if (user?.empresa_id) {
      data.empresa_id = user.empresa_id;
    }
    if (user?.id) {
      data.user_id = user.id;
    }
    return data;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      if (editing) {
        await api.post(`/despesas/${editing.id}`, payload());
        await notify("Despesa actualizada com sucesso.", "success");
      } else {
        await api.post("/despesas", payload());
        await notify("Despesa cadastrada com sucesso.", "success");
      }

      setForm(emptyForm);
      setEditing(null);
      closeModal("despesaFormModal");
      fetchDespesas();
    } catch (err) {
      await notify(err.response?.data?.message || "Não foi possível guardar a despesa.", "error");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!selected) return;
    const question = await confirmAction({
      title: "Excluir despesa",
      text: `Deseja excluir a despesa ${selected.descricao}?`,
      icon: "warning",
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
    });

    if (!question.isConfirmed) return;

    try {
      setLoading(true);
      await api.delete(`/despesas/${selected.id}`);
      await notify("Despesa excluída com sucesso.", "success");
      setSelected(null);
      fetchDespesas();
    } catch (err) {
      await notify(err.response?.data?.message || "Não foi possível excluir a despesa.", "error");
    } finally {
      setLoading(false);
    }
  };

  const canManage = user?.nivel === "admin" || user?.nivel === "operador";

  return (
    <ResourcePage
      title="Despesas"
      breadcrumb={["Gestão", "Despesas"]}
      actions={(
        <>
          <button className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1" type="button" data-bs-toggle="modal" data-bs-target="#despesaFormModal" onClick={openCreate} disabled={!canManage}>
            <Plus size={16} />
            Cadastrar
          </button>
          <button className="btn btn-light btn-sm d-inline-flex align-items-center gap-1" type="button" onClick={() => openEdit()} disabled={!canManage || !selected}>
            <Edit2 size={16} />
            Editar
          </button>
          <button className="btn btn-light text-danger btn-sm d-inline-flex align-items-center gap-1" type="button" onClick={confirmDelete} disabled={!canManage || !selected}>
            <Trash2 size={16} />
            Excluir
          </button>
        </>
      )}
    >
      <div className="card app-card bg-white">
        <div className="card-body">
          {error && <div className="alert alert-danger py-2">{error}</div>}
          {!canManage && <div className="alert alert-warning py-2">Não tem permissões para gerir despesas.</div>}

          <DataTable
            id="despesasDataTable"
            columns={["Descrição", "Referência", "Valor", "Data", "Estado"]}
            rows={despesas}
            renderRow={(item) => (
              <tr key={item.id} className={selected?.id === item.id ? "table-active" : ""} onClick={() => setSelected(item)}>
                <td>{item.descricao}</td>
                <td>{item.referencia || "-"}</td>
                <td>{Number(item.valor).toFixed(2)} Kz</td>
                <td>{item.data}</td>
                <td>
                  <span className={`badge ${item.estado === "Paga" ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger"}`}>
                    {item.estado}
                  </span>
                </td>
              </tr>
            )}
          />
        </div>
      </div>

      <AppModal
        id="despesaFormModal"
        title={editing ? "Editar despesa" : "Cadastrar despesa"}
        footer={(
          <>
            <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancelar</button>
            <button type="submit" className="btn btn-primary" form="despesaForm" disabled={loading || !canManage}>
              {loading ? "A guardar..." : "Guardar"}
            </button>
          </>
        )}
      >
        <form id="despesaForm" onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-12 mb-3">
              <label className="form-label" htmlFor="descricao">Descrição</label>
              <input className="form-control" id="descricao" name="descricao" value={form.descricao} onChange={handleChange} required placeholder="Ex: Compra de materiais de limpeza" />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="referencia">Referência</label>
              <input className="form-control" id="referencia" name="referencia" value={form.referencia} onChange={handleChange} placeholder="Ex: FT 2026/01" />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="valor">Valor (Kz)</label>
              <input className="form-control" type="number" step="0.01" id="valor" name="valor" value={form.valor} onChange={handleChange} required placeholder="0.00" />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="data">Data</label>
              <input className="form-control" type="date" id="data" name="data" value={form.data} onChange={handleChange} required />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="estado">Estado</label>
              <select className="form-select" id="estado" name="estado" value={form.estado} onChange={handleChange} required>
                <option value="Paga">Paga</option>
                <option value="Anulada">Anulada</option>
              </select>
            </div>
          </div>
        </form>
      </AppModal>
    </ResourcePage>
  );
};

export default Despesas;
